import React from 'react';
import './GraphAnimations.css';
import { useTranslation } from 'react-i18next';

interface GraphLoadingOverlayProps {
  /** 0-100 */
  progress: number;
  /** Whether the overlay should be visible */
  visible: boolean;
  /** Dark mode toggle */
  isDarkMode: boolean;
}

/**
 * Animated network-graph loading overlay shown during initial stabilization.
 *
 * – Renders a subtle animated "network" background (SVG dots + lines)
 * – Shows a circular progress ring with percentage
 * – Smoothly fades out when `visible` turns false
 */
const GraphLoadingOverlay: React.FC<GraphLoadingOverlayProps> = ({
  progress,
  visible,
  isDarkMode,
}) => {
  const { t } = useTranslation();
  const mode = isDarkMode ? 'dark-mode' : 'light-mode';
  const accent = isDarkMode ? '#60a5fa' : '#3b82f6';

  // Progress ring geometry (small 28×28 ring)
  const radius = 10;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div
      className={`graph-loading-overlay ${mode} ${!visible ? 'fade-out' : ''}`}
      style={{ pointerEvents: visible ? 'auto' : 'none' }}
    >
      {/* Compact progress indicator (bottom-right) */}
      <div className="graph-progress-container">
        <div className="graph-progress-ring">
          <svg viewBox="0 0 24 24">
            {/* Background track */}
            <circle
              className="graph-progress-ring-bg"
              cx="12"
              cy="12"
              r={radius}
              stroke={accent}
            />
            {/* Pulse effect */}
            <circle
              className="graph-progress-ring-pulse"
              cx="12"
              cy="12"
              r={radius}
              stroke={accent}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
            {/* Filled arc */}
            <circle
              className="graph-progress-ring-fill"
              cx="12"
              cy="12"
              r={radius}
              stroke={accent}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
        </div>

        <div className={`graph-progress-label ${mode}`}>
          {t('graph.generatingGraph', { progress: Math.round(progress) })}
        </div>
      </div>
    </div>
  );
};

export default GraphLoadingOverlay;
